import {useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {useCreateStep} from '../../../core/repositories/mypageEntryManagementsRepository';
import {ENTRY_SOURCE_TYPE_MANUAL} from '../constants/entrySourceTypes';
import {invalidateEntryManagementCaches} from './cacheInvalidationHelpers';
import {useProgressStepForm} from './useProgressStepForm';

type UseProgressStepActionsOptions = {
  onStepCreated?: (stepID: number, userEntryManagementID: number) => void;
  /** 現在のエントリー完了状態（ステップ追加時に状態を保持するため） */
  hasCompletedEntry?: boolean;
};

export const useProgressStepActions = (
  options?: UseProgressStepActionsOptions,
) => {
  const form = useProgressStepForm();
  const queryClient = useQueryClient();
  const createStepMutation = useCreateStep();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (
    companyID: number | undefined,
    companyName: string,
  ) => {
    if (!companyName) {
      setErrorMessage('企業名が指定されていません。');
      return {success: false};
    }

    if (!form.validate()) {
      return {success: false};
    }

    try {
      // POST /steps でステップ作成
      const result = await createStepMutation.mutateAsync({
        companyID,
        companyName,
        stepName: form.formState.data.stepName,
        entrySourceType: ENTRY_SOURCE_TYPE_MANUAL,
        notes: form.formState.data.notes || undefined,
        deadline: form.formState.data.deadline || undefined,
        startDate: form.formState.data.startDate || undefined,
        endDate: form.formState.data.endDate || undefined,
        place: form.formState.data.place || undefined,
        hasCompletedEntry: options?.hasCompletedEntry,
      });

      if (result.ok) {
        // キャッシュ無効化
        await invalidateEntryManagementCaches(
          queryClient,
          result.value.userEntryManagementID,
        );

        setSuccessMessage('選考予定を追加しました。');

        // ステップ作成成功後のコールバック（AI TODO生成用）
        if (options?.onStepCreated) {
          options.onStepCreated(
            result.value.id,
            result.value.userEntryManagementID,
          );
        }

        return {
          success: true,
          googleSyncFailed: result.value.googleSyncResult?.success === false,
        };
      }

      setErrorMessage('ステップの追加に失敗しました。');
      return {success: false};
    } catch (_error) {
      setErrorMessage('ステップの追加に失敗しました。');
      return {success: false};
    }
  };

  const clearMessages = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  return {
    form,
    handleSubmit,
    isLoading: createStepMutation.isLoading || form.formState.isSubmitting,
    successMessage,
    errorMessage,
    clearMessages,
  };
};
